// =============================================================================
// Role Types for Roles Settings Page
// Data source: MongoDB via GET /v4/legacy/roles API
// =============================================================================

/**
 * Permission entry in a role
 */
export interface Permission {
  k: string; // Permission key (e.g., 'invoices.create', 'sensors.read')
  v: 'allow' | 'deny'; // Policy
}

/**
 * Populated user who created the role
 */
export interface PopulatedUser {
  _id: string;
  firstname?: string;
  lastname?: string;
  email?: string;
}

/**
 * Role data from the V3 API
 */
export interface Role {
  _id: string;
  roleId: string;
  permissions: Permission[];
  created_by?: string | PopulatedUser;
  created_at?: string;
  deleted?: boolean;
}

/**
 * Filters for querying roles
 */
export interface RolesFilters {
  search?: string;
  sortKey?: 'roleId' | 'created_at';
  sortDirection?: 1 | -1;
  pageNumber?: number;
  pageLength?: number;
}

/**
 * API response for GET /v4/legacy/roles
 */
export interface V3RolesResponse {
  roles: Role[];
  pageNumber: number;
  pageLength: number;
  sortKey: string;
  sortDirection: number;
}

/**
 * Paginated response wrapper
 */
export interface RolesResponse {
  roles: Role[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

/**
 * Input for creating a role
 */
export interface CreateRoleInput {
  roleId: string;
  permissions: Permission[];
}

/**
 * Input for updating a role
 */
export interface UpdateRoleInput {
  roleId?: string;
  permissions?: Permission[];
}

/**
 * Schema model info from API
 */
export interface SchemaModel {
  name: string;
  collection: string;
}

/**
 * Schema field info from API
 */
export interface SchemaField {
  name: string;
  type: string;
  required?: boolean;
}

/**
 * Available actions for permissions
 */
export const PERMISSION_ACTIONS = ['read', 'create', 'modify', 'delete'] as const;
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

/**
 * Available policy values
 */
export const PERMISSION_POLICIES = ['allow', 'deny'] as const;
export type PermissionPolicy = (typeof PERMISSION_POLICIES)[number];

// =============================================================================
// RBAC Permission Constants
// =============================================================================

export const ROLES_PERMISSIONS = {
  READ: 'roles.read',
  CREATE: 'roles.create',
  MODIFY: 'roles.modify',
  DELETE: 'roles.delete',
} as const;

/**
 * Per-column read permissions for RBAC guards
 */
export const ROLES_READ_PERMISSIONS = {
  ROLE_ID: 'roles.read.roleId',
  PERMISSIONS: 'roles.read.permissions',
  CREATED_BY: 'roles.read.created_by',
  CREATED_AT: 'roles.read.created_at',
} as const;

// =============================================================================
// Column Visibility
// =============================================================================

export type RoleColumnKey = 'roleId' | 'permissions' | 'createdBy' | 'createdAt';

export const ALL_ROLE_COLUMNS: RoleColumnKey[] = [
  'roleId',
  'permissions',
  'createdBy',
  'createdAt',
];

export const DEFAULT_VISIBLE_COLUMNS: RoleColumnKey[] = [
  'roleId',
  'permissions',
  'createdBy',
  'createdAt',
];

export const COLUMN_LABELS: Record<RoleColumnKey, string> = {
  roleId: 'table.roleId',
  permissions: 'table.permissions',
  createdBy: 'table.createdBy',
  createdAt: 'table.createdAt',
};

// =============================================================================
// Structured Conditions
// =============================================================================

export const CONDITION_OPERATORS = ['=', '!=', '<', '<=', '>', '>='] as const;
export type ConditionOperator = (typeof CONDITION_OPERATORS)[number];

export interface Condition {
  field: string;
  operator: ConditionOperator;
  comparator: string;
}

// =============================================================================
// Model Exclusions / Additions
// =============================================================================

/**
 * Models excluded from the permission builder (internal/system models)
 */
export const EXCLUDED_MODELS = [
  'sessions',
  'migrations',
  'system',
] as const;

/**
 * Additional models not in schema but available for permissions
 */
export const ADDITIONAL_MODELS = [
  'accounts',
  'alerts',
  'procedures',
  'projects',
  'tickets',
] as const;

// =============================================================================
// Changelog Types
// =============================================================================

export interface ChangelogChange {
  field: string;
  old?: unknown;
  new?: unknown;
}

export interface ChangelogEntry {
  _id?: string;
  timestamp: string;
  user: {
    firstname?: string;
    lastname?: string;
  } | null;
  isOriginal: boolean;
  changes: ChangelogChange[];
}

export interface RoleChangelogResponse {
  changelog: ChangelogEntry[];
}
